import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2, AlertCircle, Mail, Shield, Zap } from 'lucide-react';
import { auth } from '../utils/auth';
import TwoFactorLogin from '../components/TwoFactorLogin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Elegant Background Component - matching landing page
const ElegantBackground = () => {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 1 }}>
      {/* Primary orb - top right */}
      <motion.div
        className="absolute -top-[20%] -right-[10%] w-[800px] h-[800px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0.05) 40%, transparent 70%)',
        }}
        animate={{
          x: [0, 30, 0],
          y: [0, 20, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      {/* Secondary orb - bottom left */}
      <motion.div
        className="absolute -bottom-[30%] -left-[15%] w-[900px] h-[900px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.12) 0%, rgba(99, 102, 241, 0.04) 40%, transparent 70%)',
        }}
        animate={{
          x: [0, -20, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      {/* Accent orb - center */}
      <motion.div
        className="absolute top-[30%] left-[40%] w-[600px] h-[600px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.08) 0%, transparent 60%)',
        }}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </div>
  );
};

// Translations
const translations = {
  nb: {
    welcomeBack: 'Velkommen tilbake',
    loginToContinue: 'Logg inn for å fortsette til ditt dashboard',
    connectingMicrosoft: 'Kobler til Microsoft...',
    loginWithMicrosoft: 'Logg inn med Microsoft',
    connectingGoogle: 'Kobler til Google...',
    loginWithGoogle: 'Logg inn med Google',
    or: 'eller',
    loginWithEmail: 'Logg inn med e-post',
    secureLogin: 'Sikker innlogging med 2FA',
    backToOptions: 'Tilbake til valg',
    email: 'E-post',
    password: 'Passord',
    yourPassword: 'Ditt passord',
    forgotPassword: 'Glemt passord?',
    forgotPasswordAlert: 'Glemt passord-funksjon kommer snart. Kontakt support for hjelp.',
    loggingIn: 'Logger inn...',
    loginButton: 'Logg inn →',
    noAccount: 'Har du ikke en konto?',
    registerFree: 'Registrer deg gratis',
    backToHome: '← Tilbake til forsiden',
    wrongCredentials: 'Feil e-post eller passord. Prøv igjen.',
    googleLoginFailed: 'Google-innlogging feilet. Prøv igjen.',
    microsoftLoginFailed: 'Microsoft-innlogging feilet. Prøv igjen.',
    loginSuccess: 'Innlogging vellykket!',
    closeWindow: 'Du kan lukke dette vinduet.',
    // Feature cards
    aiProductivity: 'AI-drevet produktivitet',
    aiProductivityDesc: 'Lexy gjør 90% av arbeidet. Du fokuserer på strategi og klientrelasjoner.',
    faster: '95% raskere',
    fasterDesc: 'Fra 12 timer til 12 minutter. Dokumentproduksjon i lynets hastighet.',
    securityFirst: 'Sikkerhet først',
    securityFirstDesc: 'GDPR-compliant. Bank-grade sikkerhet. Norske servere.'
  },
  en: {
    welcomeBack: 'Welcome back',
    loginToContinue: 'Log in to continue to your dashboard',
    connectingMicrosoft: 'Connecting to Microsoft...',
    loginWithMicrosoft: 'Log in with Microsoft',
    connectingGoogle: 'Connecting to Google...',
    loginWithGoogle: 'Log in with Google',
    or: 'or',
    loginWithEmail: 'Log in with email',
    secureLogin: 'Secure login with 2FA',
    backToOptions: 'Back to options',
    email: 'Email',
    password: 'Password',
    yourPassword: 'Your password',
    forgotPassword: 'Forgot password?',
    forgotPasswordAlert: 'Forgot password feature coming soon. Contact support for help.',
    loggingIn: 'Logging in...',
    loginButton: 'Log in →',
    noAccount: "Don't have an account?",
    registerFree: 'Register for free',
    backToHome: '← Back to homepage',
    wrongCredentials: 'Wrong email or password. Please try again.',
    googleLoginFailed: 'Google login failed. Please try again.',
    microsoftLoginFailed: 'Microsoft login failed. Please try again.',
    loginSuccess: 'Login successful!',
    closeWindow: 'You can close this window.',
    // Feature cards
    aiProductivity: 'AI-powered productivity',
    aiProductivityDesc: 'Lexy does 90% of the work. You focus on strategy and client relationships.',
    faster: '95% faster',
    fasterDesc: 'From 12 hours to 12 minutes. Document production at lightning speed.',
    securityFirst: 'Security first',
    securityFirstDesc: 'GDPR-compliant. Bank-grade security. Norwegian servers.'
  }
};

// Microsoft Logo SVG Component
const MicrosoftLogo = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
  </svg>
);

// Google Logo SVG Component
const GoogleLogo = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

// Language selector removed - using route-based language instead

interface LexyLoginPageProps {
  lang?: 'nb' | 'en';
}

const LexyLoginPage: React.FC<LexyLoginPageProps> = ({ lang = 'nb' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  
  // Use prop-based language
  const t = translations[lang];
  
  // Language preference removed - using route-based language
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState<{email: string; password: string} | null>(null);

  // Handle token from OAuth redirect
  useEffect(() => {
    const token = searchParams.get('token');
    const oauthError = searchParams.get('error');
    const provider = searchParams.get('provider');
    const fromWordAddin = searchParams.get('from') === 'word-addin';
    
    if (oauthError) {
      const errorMsg = provider === 'google' 
        ? t.googleLoginFailed 
        : t.microsoftLoginFailed;
      setError(errorMsg);
    }
    
    if (token) {
      localStorage.setItem('token', token);
      localStorage.setItem('lexy_token', token);
      fetchUserAndRedirect(token, fromWordAddin);
    }
    
    if (provider && !token && !oauthError) {
      if (provider === 'google') {
        window.location.href = `${API}/auth/google/login`;
      } else if (provider === 'microsoft') {
        window.location.href = `${API}/auth/microsoft/login`;
      }
    }
  }, [searchParams, t]);
  
  const fetchUserAndRedirect = async (token: string, closeWindow: boolean = false) => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const user = await response.json();
        localStorage.setItem('user', JSON.stringify(user));
        
        if (closeWindow && window.opener) {
          document.body.innerHTML = `
            <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: Inter, sans-serif;">
              <div style="width: 60px; height: 60px; background: linear-gradient(135deg, #8b5cf6, #7c3aed); border-radius: 16px; display: flex; align-items: center; justify-content: center; margin-bottom: 16px;">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><polyline points="20,6 9,17 4,12"/></svg>
              </div>
              <h2 style="color: #1e293b; margin-bottom: 8px;">${t.loginSuccess}</h2>
              <p style="color: #64748b;">${t.closeWindow}</p>
            </div>
          `;
          setTimeout(() => window.close(), 1500);
          return;
        }
        
        navigate('/lexy/dashboard');
      }
    } catch (err) {
      console.error('Failed to fetch user:', err);
    }
  };

  const handleLoginSuccess = (user: any) => {
    if (user.role === 'LAWYER' || user.role === 'ADMIN' || user.role === 'TENANT_ADMIN') {
      navigate('/lexy/dashboard');
    } else if (user.role === 'USER') {
      navigate('/cases');
    } else {
      navigate('/');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    setError('');

    try {
      const result = await auth.login(email, password);
      
      if (result?.requires_2fa) {
        setPendingCredentials({ email, password });
        setRequires2FA(true);
        return;
      }
      
      if (result?.requires_2fa_setup) {
        localStorage.setItem('user', JSON.stringify(result.user || result));
        if (result.token) {
          localStorage.setItem('token', result.token);
        }
        navigate('/lexy/setup-2fa');
        return;
      }
      
      handleLoginSuccess(result);
      
    } catch (err: any) {
      setError(err.response?.data?.detail || t.wrongCredentials);
    } finally {
      setLoading(false);
    }
  };

  const handle2FASuccess = async (data: any) => {
    handleLoginSuccess(data.user);
  };

  const handle2FABack = () => {
    setRequires2FA(false);
    setPendingCredentials(null);
  };
  
  const handleMicrosoftLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMicrosoftLoading(true);
    window.location.href = `${API}/auth/microsoft/login`;
  };

  const handleGoogleLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setGoogleLoading(true);
    window.location.href = `${API}/auth/google/login`;
  };

  const handleEmailLoginClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowEmailForm(true);
  };

  const handleBackClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowEmailForm(false);
  };

  if (requires2FA && pendingCredentials) {
    return (
      <TwoFactorLogin
        email={pendingCredentials.email}
        password={pendingCredentials.password}
        onSuccess={handle2FASuccess}
        onBack={handle2FABack}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#08080a] relative overflow-hidden">
      {/* Animated gradient background - matching landing page */}
      <ElegantBackground />
      
      <div className="min-h-screen flex items-center justify-center p-8 relative" style={{ zIndex: 2 }}>
        <div className="w-full max-w-sm">
          {/* Logo - minimalist */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-2.5 mb-12"
          >
            <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Zap className="w-4.5 h-4.5 text-white" strokeWidth={2.5} />
            </div>
            <span className="text-xl font-medium text-white tracking-tight">Lexy</span>
          </motion.div>
          
          {/* Header - elegant typography */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="text-2xl font-light text-white mb-2 tracking-tight">{t.welcomeBack}</h1>
            <p className="text-white/40 text-sm mb-10">{t.loginToContinue}</p>
          </motion.div>

          {!showEmailForm ? (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-3"
            >
              {/* Google Button - minimalist */}
              <button
                onClick={handleGoogleLogin}
                disabled={googleLoading || microsoftLoading}
                className="w-full h-11 flex items-center justify-center gap-3 bg-white/95 text-neutral-700 rounded-xl text-[14px] font-medium hover:bg-white transition-all duration-300 disabled:opacity-50"
              >
                {googleLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {t.connectingGoogle}</>
                ) : (
                  <><GoogleLogo /> {t.loginWithGoogle}</>
                )}
              </button>

              {/* Microsoft Button - minimalist */}
              <button
                onClick={handleMicrosoftLogin}
                disabled={microsoftLoading || googleLoading}
                className="w-full h-11 flex items-center justify-center gap-3 bg-white/95 text-neutral-700 rounded-xl text-[14px] font-medium hover:bg-white transition-all duration-300 disabled:opacity-50"
              >
                {microsoftLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {t.connectingMicrosoft}</>
                ) : (
                  <><MicrosoftLogo /> {t.loginWithMicrosoft}</>
                )}
              </button>

              {/* Divider - subtle */}
              <div className="flex items-center gap-4 py-4">
                <div className="flex-1 h-px bg-white/10"></div>
                <span className="text-white/30 text-xs">{t.or}</span>
                <div className="flex-1 h-px bg-white/10"></div>
              </div>

              {/* Email Login Button - ghost style */}
              <button
                onClick={handleEmailLoginClick}
                className="w-full h-11 flex items-center justify-center gap-2.5 bg-white/5 border border-white/10 text-white/70 rounded-xl text-[14px] font-medium hover:bg-white/10 hover:text-white/90 hover:border-white/20 transition-all duration-300"
              >
                <Mail className="w-4 h-4" />
                {t.loginWithEmail}
              </button>

              {/* Security note - subtle */}
              <div className="flex items-center justify-center gap-2 pt-6 text-white/30 text-xs">
                <Shield className="w-3.5 h-3.5 text-emerald-500/70" />
                {t.secureLogin}
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* Back button - minimal */}
              <button
                onClick={handleBackClick}
                className="flex items-center gap-1.5 text-white/40 hover:text-white/70 mb-8 text-sm transition-all duration-300"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                {t.backToOptions}
              </button>

              {/* Email Form - clean */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-white/40 text-xs mb-2 tracking-wide">{t.email}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john@lawfirm.com"
                    required
                    className="w-full h-11 px-4 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-white/25 focus:bg-white/[0.07] transition-all duration-300"
                  />
                </div>

                <div>
                  <label className="block text-white/40 text-xs mb-2 tracking-wide">{t.password}</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t.yourPassword}
                    required
                    className="w-full h-11 px-4 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-white/25 focus:bg-white/[0.07] transition-all duration-300"
                  />
                </div>

                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => alert(t.forgotPasswordAlert)}
                    className="text-white/30 hover:text-white/60 text-xs transition-all duration-300"
                  >
                    {t.forgotPassword}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/20 hover:border-white/30 text-white rounded-xl text-sm font-medium transition-all duration-300 disabled:opacity-50"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> {t.loggingIn}</>
                  ) : (
                    t.loginButton
                  )}
                </button>
              </form>
            </motion.div>
          )}

          {/* Register link - subtle */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 text-center"
          >
            <span className="text-white/30 text-sm">{t.noAccount} </span>
            <button
              onClick={() => navigate('/lexy/signup')}
              className="text-white/70 hover:text-white text-sm font-medium transition-all duration-300"
            >
              {t.registerFree}
            </button>
          </motion.div>

          {/* Back to home - very subtle */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-4 text-center"
          >
            <button
              onClick={() => navigate('/')}
              className="text-white/20 hover:text-white/40 text-xs transition-all duration-300"
            >
              {t.backToHome}
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LexyLoginPage;
